/**
 * Shared types for the Shalom App Control admin API client.
 *
 * These mirror docs/API_CONTRACT.md, docs/MOBILE_CONFIG_CONTRACT.md and
 * docs/DATA_MODEL.md. Do not invent fields that are not backed by those
 * documents - the frontend must not assume a contract the backend does not
 * implement.
 */

// ---------------------------------------------------------------------------
// App mode / feature flags / Olivia (docs/MOBILE_CONFIG_CONTRACT.md)
// ---------------------------------------------------------------------------

export const APP_MODES = [
  "normal",
  "read_only",
  "maintenance",
  "force_update",
  "disabled",
] as const;
export type AppMode = (typeof APP_MODES)[number];

/** Modes that require the strong, double-confirmation danger dialog. */
export const DANGEROUS_APP_MODES: readonly AppMode[] = [
  "maintenance",
  "force_update",
  "disabled",
];

export const OLIVIA_ACCESS_MODES = ["disabled", "public", "members"] as const;
export type OliviaAccessMode = (typeof OLIVIA_ACCESS_MODES)[number];

export interface OliviaConfig {
  enabled: boolean;
  accessMode: OliviaAccessMode;
}

export interface FeatureFlags {
  home: boolean;
  bible: boolean;
  dailyVerse: boolean;
  dailyReflection: boolean;
  locations: boolean;
  ministries: boolean;
  events: boolean;
  podcast: boolean;
  contact: boolean;
  prepareVisit: boolean;
  firstSteps: boolean;
  notifications: boolean;
  olivia: boolean;
}

export const FEATURE_FLAG_KEYS: (keyof FeatureFlags)[] = [
  "home",
  "bible",
  "dailyVerse",
  "dailyReflection",
  "locations",
  "ministries",
  "events",
  "podcast",
  "contact",
  "prepareVisit",
  "firstSteps",
  "notifications",
  "olivia",
];

export const FEATURE_FLAG_LABELS: Record<keyof FeatureFlags, string> = {
  home: "Inicio",
  bible: "Biblia",
  dailyVerse: "Versiculo diario",
  dailyReflection: "Reflexion diaria",
  locations: "Ubicaciones",
  ministries: "Ministerios",
  events: "Eventos",
  podcast: "Podcast",
  contact: "Contacto",
  prepareVisit: "Preparar visita",
  firstSteps: "Primeros pasos",
  notifications: "Notificaciones",
  olivia: "Olivia",
};

// ---------------------------------------------------------------------------
// Global config (docs/API_CONTRACT.md - Global Config, docs/DATA_MODEL.md)
// ---------------------------------------------------------------------------

export interface GlobalConfigInput {
  appMode: AppMode;
  appEnabled: boolean;
  maintenanceMessage: string | null;
  minimumVersion: string;
  minimumVersionCode: number;
  forceUpdate: boolean;
  features: FeatureFlags;
  olivia: OliviaConfig;
  /** Optional operator-supplied reason, stored with the new version. */
  changeReason?: string | null;
  confirmationToken?: string | null;
}

export interface EmergencyConfirmationRequest {
  password: string;
  totpCode: string;
  targetMode: Extract<AppMode, "maintenance" | "force_update" | "disabled">;
}

export interface EmergencyConfirmationResponse {
  confirmationToken: string;
}

export interface GlobalConfigVersion extends GlobalConfigInput {
  id: string;
  revision: number;
  createdByAdminId: string | null;
  createdByEmail: string | null;
  createdAt: string;
  sourceVersionId: string | null;
}

// ---------------------------------------------------------------------------
// Home blocks (docs/MOBILE_CONFIG_CONTRACT.md, docs/DATA_MODEL.md)
// ---------------------------------------------------------------------------

export const HOME_BLOCK_TYPES = [
  "hero",
  "weekly_schedule",
  "media",
  "next_steps",
  "quick_actions",
  "community",
  "bible",
  "daily_verse",
  "daily_reflection",
  "event",
  "announcement",
  "first_steps",
  "prepare_visit",
  "custom_banner",
  "image_banner",
] as const;
export type HomeBlockType = (typeof HOME_BLOCK_TYPES)[number];

export const HOME_BLOCK_TYPE_LABELS: Record<HomeBlockType, string> = {
  hero: "Hero",
  weekly_schedule: "Agenda semanal",
  media: "Contenido multimedia",
  next_steps: "Caminos",
  quick_actions: "Accesos rapidos",
  community: "Comunidad",
  bible: "Biblia",
  daily_verse: "Versiculo diario",
  daily_reflection: "Reflexion diaria",
  event: "Evento",
  announcement: "Anuncio",
  first_steps: "Primeros pasos",
  prepare_visit: "Preparar visita",
  custom_banner: "Banner personalizado",
  image_banner: "Anuncio con imagen",
};

export const HOME_BLOCK_STATUSES = ["draft", "published"] as const;
export type HomeBlockStatus = (typeof HOME_BLOCK_STATUSES)[number];
export type HomeBlockEffectiveStatus = "draft" | "scheduled" | "active" | "finished" | "disabled";

export interface HomeBlock {
  id: string;
  type: HomeBlockType;
  source: "system" | "custom";
  label: string | null;
  enabled: boolean;
  order: number;
  title: string | null;
  subtitle: string | null;
  body: string | null;
  imageUrl: string | null;
  actionLabel: string | null;
  actionTarget: string | null;
  startsAt: string | null;
  endsAt: string | null;
  status: HomeBlockStatus;
  effectiveStatus?: HomeBlockEffectiveStatus;
  metadata: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  updatedByEmail?: string | null;
}

export type HomeBlockInput = Omit<
  HomeBlock,
  "id" | "source" | "label" | "effectiveStatus" | "createdAt" | "updatedAt" | "updatedByEmail"
>;

export interface HomeBlockReorderItem {
  id: string;
  order: number;
}

// ---------------------------------------------------------------------------
// Daily content (docs/API_CONTRACT.md - Daily Content, docs/DATA_MODEL.md)
// ---------------------------------------------------------------------------

export const DAILY_CONTENT_STATUSES = [
  "available",
  "draft",
  "published",
  "failed",
  "archived",
  "not_found",
] as const;
export type DailyContentStatus = (typeof DAILY_CONTENT_STATUSES)[number];

export interface DailyVerse {
  id?: string;
  date?: string;
  status: DailyContentStatus;
  reference: string | null;
  text: string | null;
  generation?: Record<string, unknown> | null;
  imageUrl: string | null;
  imageMetadata?: Record<string, unknown> | null;
  source?: string;
  createdAt?: string;
  updatedAt: string | null;
}

export interface DailyReflection {
  id?: string;
  date: string;
  status: DailyContentStatus;
  title: string | null;
  body: string | null;
  sourceVerseReference?: string | null;
  sourceVerseText?: string | null;
  model?: string | null;
  mode?: "automatic" | "manual" | string;
  generatedAt?: string | null;
  generationAttempts?: number | null;
  generation?: Record<string, unknown> | null;
  estimatedCost: number | null;
  estimatedCostUsd?: number | null;
  error?: string | null;
  source?: string;
  createdAt?: string;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Notifications (docs/API_CONTRACT.md - Notifications, docs/DATA_MODEL.md)
// ---------------------------------------------------------------------------

export const NOTIFICATION_STATUSES = [
  "draft",
  "pending",
  "processing",
  "scheduled",
  "sent",
  "partial",
  "cancelled",
  "failed",
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export interface NotificationAudience {
  type: "all" | "placeholder" | string;
  description?: string;
}

export interface NotificationCampaign {
  id: string;
  kind?: "job" | "campaign" | "delivery";
  campaignId?: string;
  title: string;
  body: string;
  target: string | null;
  targetType?: NotificationTargetType;
  targetId?: string | null;
  targetPath?: string | null;
  audience: NotificationAudience;
  schedule?: NotificationScheduleSpec;
  timezone?: string;
  status: NotificationStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  recipients: number | null;
  accepted: number | null;
  failed: number | null;
  providerResult: Record<string, unknown> | null;
  createdByEmail: string | null;
  createdAt: string;
  updatedAt: string;
  providerMessageId: string | null;
  errorMessage: string | null;
  deliveries?: NotificationCampaign[];
}

export type NotificationTargetType = "none" | "home" | "home_block" | "route";

export interface NotificationScheduleSpec {
  mode: "now" | "once" | "daily" | "weekdays" | "weekly";
  scheduledAt?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  time?: string | null;
  weekdays?: number[];
  timezone: "Europe/Madrid";
}

export interface NotificationCreateInput {
  title: string;
  body: string;
  target: string | null;
  targetType?: NotificationTargetType;
  targetId?: string | null;
  targetPath?: string | null;
  audience: NotificationAudience;
  scheduledAt: string | null;
  schedule?: NotificationScheduleSpec | null;
}

// ---------------------------------------------------------------------------
// Audit (docs/API_CONTRACT.md - Audit, docs/DATA_MODEL.md)
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string;
  actorAdminId: string | null;
  actorEmail: string | null;
  occurredAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  requestId: string | null;
}

export interface AuditLogFilters {
  actor?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  dateFrom?: string;
  dateTo?: string;
  requestId?: string;
  page?: number;
  pageSize?: number;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Auth (docs/API_CONTRACT.md - Auth API, docs/SECURITY.md)
// ---------------------------------------------------------------------------

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginChallengeResponse {
  challengeId: string;
  requiresTotp: boolean;
  requiresEnrollment: boolean;
  expiresAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
}

export interface TotpVerifyRequest {
  challengeId: string;
  code: string;
}

export interface RecoveryCodeVerifyRequest {
  challengeId: string;
  recoveryCode: string;
}

export interface AuthSuccessResponse {
  user: AdminUser;
  csrfToken: string;
}

export interface MeResponse {
  user: AdminUser;
  totpEnabled: boolean;
  session: {
    id: string;
    createdAt: string;
    lastSeenAt: string;
    expiresAt: string;
    ipAddress: string | null;
    userAgent: string | null;
  };
}

export interface AdminSession {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  isCurrent: boolean;
}

export interface TotpEnrollResponse {
  enrollmentId: string;
  otpauthUri: string;
  qrImage: string;
}

export interface TotpConfirmRequest {
  enrollmentId: string;
  code: string;
  challengeId?: string;
}

export interface TotpConfirmResponse {
  recoveryCodes: string[];
  csrfToken?: string;
}

// ---------------------------------------------------------------------------
// Errors (docs/API_CONTRACT.md - Errors)
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  requestId: string;
}
